import {
    buildFollowPath,
    buildUnfollowPath,
    buildOrganizersStatusPath,
    DEFAULT_API_BASE,
    normalizeBase,
} from './endpoints';
import {
    normalizeOrganizers,
    OrganizerData,
    OrganizerDataMap,
    RawOrganizer,
} from './transformations';

/**
 * Response of a follow / unfollow WRITE, normalized to camelCase. Core returns
 * `{ action_performed, followed_by_you }`; the eb-ui oracle transformed those to
 * `{ actionPerformed, followedByYou }` (see `organizer.test.js`).
 */
export interface FollowActionResult {
    actionPerformed?: boolean;
    followedByYou: boolean;
}

/** Raw follow/unfollow WRITE payload as Core returns it (snake_case). */
interface RawFollowActionResult {
    action_performed?: boolean;
    followed_by_you?: boolean;
}

/** Raw follow-status READ payload. */
interface RawOrganizersStatusResponse {
    organizers?: RawOrganizer[];
}

/**
 * A minimal same-origin fetcher the client uses to reach the consuming app's
 * OWN BFF. It is intentionally tiny so a Valkyrie app can pass its
 * `@eventbrite/webapp-api-client` (which adds eb-* auth headers + CSRF) instead
 * of the browser `fetch`, without this library depending on that package.
 *
 * Contract: resolves with the parsed JSON body on a 2xx, REJECTS on a non-2xx
 * (so react-query treats it as an error and rolls back optimistic state).
 */
export type Fetcher = (
    path: string,
    init?: { method?: 'GET' | 'POST'; body?: string },
) => Promise<unknown>;

/**
 * Default browser fetcher: same-origin, credentials included (so the BFF sees
 * the session cookie), rejects on non-2xx. A public GET needs no CSRF; the
 * follow/unfollow POSTs carry no body and rely on the BFF to attach CSRF +
 * soa-token server-side (the Valkyrie contract — the client must not touch
 * Core directly).
 */
export const defaultFetcher: Fetcher = async (path, init = {}) => {
    const response = await fetch(path, {
        method: init.method ?? 'GET',
        credentials: 'include',
        headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
        body: init.body,
    });

    if (!response.ok) {
        throw new Error(
            `follow-organizer request failed: ${init.method ?? 'GET'} ${path} → ${response.status}`,
        );
    }

    // Some POSTs may return an empty body; guard the JSON parse.
    const text = await response.text();
    return text ? JSON.parse(text) : {};
};

export interface FollowClientConfig {
    /** Injected transport (defaults to same-origin `fetch`). */
    fetcher?: Fetcher;
    /** Base path prepended to every route; defaults to `/api/v3`. */
    apiBase?: string;
}

/**
 * The follow-organizer wire client. One instance per app; the hooks below take
 * it via props/context so the SAME injected fetcher (the app's BFF client) is
 * used for every call.
 */
export class FollowOrganizerClient {
    private readonly fetcher: Fetcher;
    private readonly apiBase: string;

    constructor(config: FollowClientConfig = {}) {
        this.fetcher = config.fetcher ?? defaultFetcher;
        // Never trust a caller-supplied base with a credentialed fetcher:
        // normalize it to a same-origin path prefix so a protocol-relative,
        // absolute, or backslash-obfuscated base can never point the
        // credentialed fetch off-origin (session-cookie leak). Off-origin or
        // malformed values fall back to DEFAULT_API_BASE.
        this.apiBase = normalizeBase(config.apiBase ?? DEFAULT_API_BASE);
    }

    /** POST follow — id in path, no body. */
    async followOrganizer(organizerId: string): Promise<FollowActionResult> {
        const raw = (await this.fetcher(
            buildFollowPath(organizerId, this.apiBase),
            { method: 'POST' },
        )) as RawFollowActionResult;
        return normalizeFollowAction(raw);
    }

    /** POST unfollow — id in path, no body. POST, NOT DELETE. */
    async unfollowOrganizer(organizerId: string): Promise<FollowActionResult> {
        const raw = (await this.fetcher(
            buildUnfollowPath(organizerId, this.apiBase),
            { method: 'POST' },
        )) as RawFollowActionResult;
        return normalizeFollowAction(raw);
    }

    /** GET follow-status for a set of organizers → normalized camelCase map. */
    async getOrganizersStatus(
        organizerIds: string[],
    ): Promise<OrganizerDataMap> {
        const raw = (await this.fetcher(
            buildOrganizersStatusPath(organizerIds, this.apiBase),
        )) as RawOrganizersStatusResponse;
        return normalizeOrganizers(raw.organizers ?? []);
    }
}

const normalizeFollowAction = (
    raw: RawFollowActionResult,
): FollowActionResult => ({
    actionPerformed: raw.action_performed,
    followedByYou: Boolean(raw.followed_by_you),
});

export type { OrganizerData, OrganizerDataMap };
